/**
 * ============================================================
 * VERO — PDP Gallery: Sticky Sidebar Scroll Effect
 * File: assets/vero-pdp-gallery.js
 * ============================================================
 * IntersectionObserver to track visible gallery image
 * Updates scroll indicator bar position
 */

(function () {
  'use strict';

  const gallery = {
    container: document.getElementById('pdp-gallery'),
    images: document.querySelectorAll('.pdp-gallery__img'),
    indicatorBar: document.getElementById('indicator-bar'),
    currentIndex: 0,

    init() {
      if (!this.container || !this.images.length) return;

      this.setupIntersectionObserver();
      this.setupScrollListener();
    },

    setupIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '-25% 0px -75% 0px',
        threshold: 0
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            this.updateIndicator(index);
          }
        });
      }, options);

      this.images.forEach(img => {
        observer.observe(img);
      });
    },

    setupScrollListener() {
      // Fallback: update indicator on scroll event
      let scrollTimeout;
      window.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.updateIndicatorOnScroll();
        }, 100);
      }, { passive: true });
    },

    updateIndicator(index) {
      this.currentIndex = index;
      const total = this.images.length;
      const percentage = ((index + 1) / total) * 100;

      if (this.indicatorBar) {
        this.indicatorBar.style.height = percentage + '%';
      }
    },

    updateIndicatorOnScroll() {
      // Find which image is most visible
      let mostVisible = 0;
      let maxVisibility = 0;

      this.images.forEach((img, index) => {
        const rect = img.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Calculate visibility percentage
        const top = Math.max(0, rect.top);
        const bottom = Math.min(viewportHeight, rect.bottom);
        const visibleHeight = Math.max(0, bottom - top);
        const visibility = visibleHeight / Math.min(rect.height, viewportHeight);

        if (visibility > maxVisibility) {
          maxVisibility = visibility;
          mostVisible = index;
        }
      });

      if (mostVisible !== this.currentIndex) {
        this.updateIndicator(mostVisible);
      }
    }
  };

  /* ─── VARIANT SELECTOR ──────────────────────────────── */
  const variants = {
    form: document.getElementById('vero-product-form'),
    variantInput: document.getElementById('vero-variant-id'),
    priceEl: document.getElementById('vero-price'),
    addBtn: document.getElementById('vero-add-to-cart'),

    init() {
      if (!this.form) return;

      // Handle option changes
      document.querySelectorAll('input[data-option-position]').forEach(input => {
        input.addEventListener('change', () => {
          this.updateVariant();
        });
      });

      // Set initial variant if URL has ?variant=id
      const url = new URL(window.location);
      const variantId = url.searchParams.get('variant');
      if (variantId) {
        this.selectVariantById(variantId);
      }
    },

    selectVariantById(variantId) {
      const variants = window.__veroVariants || [];
      const variant = variants.find(v => v.id == variantId);

      if (!variant) return;

      // Select all options for this variant
      Object.entries(variant).forEach(([key, value]) => {
        if (key.startsWith('option')) {
          const input = document.querySelector(
            `input[data-option-position="${key.replace('option', '')}"][value="${value}"]`
          );
          if (input) {
            input.checked = true;
          }
        }
      });

      this.updateVariant();
    },

    getSelectedOptions() {
      const opts = {};
      document.querySelectorAll('input[data-option-position]:checked').forEach(el => {
        opts[el.dataset.optionPosition] = el.value;
      });
      return opts;
    },

    updateVariant() {
      const opts = this.getSelectedOptions();
      const variants = window.__veroVariants || [];

      const match = variants.find(v =>
        Object.entries(opts).every(([pos, val]) => v[`option${pos}`] === val)
      );

      if (!match) return;

      this.variantInput.value = match.id;

      // Update URL
      const url = new URL(window.location);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);

      // Update price
      if (this.priceEl && match.price) {
        const currency = match.currency || 'EUR';
        const formatted = (match.price / 100).toLocaleString(
          currency === 'GBP' ? 'en-GB' : 'de-DE',
          { style: 'currency', currency }
        );
        const priceMain = this.priceEl.querySelector('.pdp-info__price-main');
        if (priceMain) priceMain.textContent = formatted;
      }

      // Update availability
      if (this.addBtn) {
        const available = match.available !== false;
        this.addBtn.disabled = !available;
        this.addBtn.querySelector('.pdp-form__btn-label').textContent =
          available ? 'Add to cart' : 'Sold out';
      }

      // Scroll to first image if variant has featured image
      if (match.featured_image && gallery.images.length) {
        const imgIndex = Array.from(gallery.images).findIndex(img =>
          img.src.includes(match.featured_image.id)
        );
        if (imgIndex > -1) {
          gallery.images[imgIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  /* ─── QUANTITY ──────────────────────────────────────── */
  const qty = {
    input: document.getElementById('vero-qty'),

    init() {
      if (!this.input) return;

      document.getElementById('vero-qty-minus')?.addEventListener('click', (e) => {
        e.preventDefault();
        const v = parseInt(this.input.value) || 1;
        if (v > 1) this.input.value = v - 1;
      });

      document.getElementById('vero-qty-plus')?.addEventListener('click', (e) => {
        e.preventDefault();
        const v = parseInt(this.input.value) || 1;
        if (v < 10) this.input.value = v + 1;
      });
    }
  };

  /* ─── ADD TO CART ───────────────────────────────────── */
  const cart = {
    form: document.getElementById('vero-product-form'),
    btn: document.getElementById('vero-add-to-cart'),

    init() {
      if (!this.form) return;

      this.form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (this.btn.disabled) return;

        this.btn.classList.add('is-loading');
        const label = this.btn.querySelector('.pdp-form__btn-label');
        const originalText = label.textContent;

        const formData = new FormData(this.form);
        const body = JSON.stringify({
          id: formData.get('id'),
          quantity: parseInt(formData.get('quantity')) || 1,
        });

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body
          });

          if (!res.ok) throw new Error('Cart error');

          // Success feedback
          this.btn.classList.remove('is-loading');
          label.textContent = 'Added ✓';
          setTimeout(() => {
            label.textContent = originalText;
          }, 2000);

          // Update cart count
          const cartRes = await fetch('/cart.js');
          const cartData = await cartRes.json();
          document.querySelectorAll('[data-cart-count]').forEach(el => {
            el.textContent = cartData.item_count;
          });

          // Dispatch event
          document.dispatchEvent(new CustomEvent('vero:cart:updated', {
            detail: cartData
          }));

        } catch (err) {
          this.btn.classList.remove('is-loading');
          label.textContent = 'Try again';
          setTimeout(() => {
            label.textContent = originalText;
          }, 2000);
        }
      });
    }
  };

  /* ─── COLOR THUMBS ─────────────────────────────────── */
  const colorThumbs = {
    colorImagesData: null,

    init() {
      // Parse color images data from JSON
      const jsonEl = document.getElementById('vero-color-images');
      if (jsonEl) {
        try {
          this.colorImagesData = JSON.parse(jsonEl.textContent);
        } catch (e) {
          console.warn('Color images data parse error:', e);
        }
      }

      // Setup click handlers
      document.querySelectorAll('.vero-color-thumb').forEach(thumb => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          this.selectColor(thumb);
        });
      });
    },

    selectColor(thumb) {
      const variantId = thumb.dataset.variantId;
      const colorName = thumb.dataset.color;

      // 1. Update visual selection
      document.querySelectorAll('.vero-color-thumb')
        .forEach(t => t.classList.remove('is-selected'));
      thumb.classList.add('is-selected');

      // 2. Update color label
      const colorLabel = document.getElementById('vero-color-label');
      if (colorLabel) {
        colorLabel.textContent = colorName;
      }

      // 3. Update variant ID and trigger variant update
      if (variants.variantInput) {
        variants.variantInput.value = variantId;

        // Find and select the variant in the form
        const variant = (window.__veroVariants || []).find(v => v.id == variantId);
        if (variant) {
          // Select matching options
          Object.entries(variant).forEach(([key, value]) => {
            if (key.startsWith('option')) {
              const input = document.querySelector(
                `input[data-option-position="${key.replace('option', '')}"][value="${value}"]`
              );
              if (input) {
                input.checked = true;
              }
            }
          });

          variants.updateVariant();
        }
      }

      // 4. Update gallery images if color images available
      if (this.colorImagesData && this.colorImagesData[colorName.toLowerCase()]) {
        this.updateGalleryForColor(colorName.toLowerCase());
      }

      // 5. Scroll to gallery top
      const galleryEl = document.getElementById('pdp-gallery');
      if (galleryEl) {
        galleryEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      // 6. Update URL
      const url = new URL(window.location);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    },

    updateGalleryForColor(colorKey) {
      const colorImages = this.colorImagesData[colorKey];
      if (!colorImages || !colorImages.length) return;

      const galleryEl = document.getElementById('pdp-gallery');
      if (!galleryEl) return;

      // Clear existing images and recreate them for this color
      const existingImages = galleryEl.querySelectorAll('.pdp-gallery__img');
      const existingIndicator = galleryEl.querySelector('.pdp-gallery__scroll-indicator');

      // Remove all images except indicator
      existingImages.forEach(img => img.remove());

      // Add color-specific images
      colorImages.forEach((imgData, index) => {
        const img = document.createElement('img');
        img.className = 'pdp-gallery__img';
        img.dataset.index = index;
        img.src = imgData.url;
        img.srcset = imgData.srcset;
        img.sizes = '(max-width: 768px) 100vw, 55vw';
        img.alt = imgData.alt;
        img.loading = index === 0 ? 'eager' : 'lazy';

        galleryEl.appendChild(img);
      });

      // Re-add indicator if it existed
      if (existingIndicator) {
        galleryEl.appendChild(existingIndicator);
      }

      // Reinitialize gallery observer with new images
      gallery.images = galleryEl.querySelectorAll('.pdp-gallery__img');
      gallery.currentIndex = 0;
      gallery.setupIntersectionObserver();
      gallery.updateIndicator(0);
    }
  };

  /* ─── INIT ──────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      gallery.init();
      variants.init();
      qty.init();
      cart.init();
      colorThumbs.init();
    });
  } else {
    gallery.init();
    variants.init();
    qty.init();
    cart.init();
    colorThumbs.init();
  }

})();
